import { Fragment, useEffect, useRef, useState } from "react";

/**
 * Shared page chrome so every tab looks and behaves the same:
 *  - PageHeader: eyebrow, page title, optional description and right-hand actions, then the sub-tab strip
 *  - SubTabs: one pill strip used everywhere -- single row, scrolls sideways with an edge fade (see
 *    lib/scrollHints.js) when it doesn't fit, keeps the active tab in view
 *  - SectionHeader: the heading for a section *inside* a tab (smaller than the page title, no eyebrow)
 */

export function SubTabs({ tabs, value, onChange, testId, itemTestId, className = "" }) {
  const ref = useRef(null);
  const [more, setMore] = useState(false);               // a phone-width strip scrolls sideways: fade its right edge while tabs are hidden past it, so nobody misses them
  const measure = () => { const el = ref.current; if (el) setMore(el.scrollWidth - el.clientWidth - el.scrollLeft > 6); };
  useEffect(() => {
    const el = ref.current?.querySelector('[aria-selected="true"]');
    // Only scroll the strip itself (not the page) so the active tab is never hidden off the edge
    if (el && ref.current && ref.current.scrollWidth > ref.current.clientWidth) {
      ref.current.scrollTo({ left: Math.max(0, el.offsetLeft - 24), behavior: "smooth" });
    }
    measure();
  }, [value]);
  useEffect(() => { measure(); window.addEventListener("resize", measure); return () => window.removeEventListener("resize", measure); }, [tabs.length]);
  // On a wide screen the tabs wrap onto a second row instead of running off the edge (Settings, Team and Finance have nine; the last ones were out of sight)
  return (
    <div ref={ref} role="tablist" data-testid={testId} onScroll={measure}
      style={more ? { WebkitMaskImage: "linear-gradient(to right, #000 calc(100% - 36px), transparent)", maskImage: "linear-gradient(to right, #000 calc(100% - 36px), transparent)" } : undefined}
      className={`no-scrollbar flex flex-nowrap w-fit max-w-full overflow-x-auto gap-1 rounded-full border border-[var(--hp-border-hi)] p-0.5 print:hidden lg:flex-wrap lg:overflow-visible lg:rounded-3xl ${className}`}>
      {tabs.map(({ id, label, icon: Icon, badge, badgeTestId, dividerBefore }) => {
        const on = value === id;
        return (
          <Fragment key={id}>
          {dividerBefore && <span aria-hidden="true" className="mx-1 my-1.5 w-px shrink-0 bg-[var(--hp-border-hi)] lg:hidden" />}
          <button role="tab" aria-selected={on} data-testid={itemTestId(id)} onClick={() => onChange(id)}
            className={`inline-flex shrink-0 whitespace-nowrap items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${on ? "bg-[var(--hp-primary)] text-[#04231a]" : "text-[var(--hp-muted)] hover:text-[var(--hp-heading)]"}`}>
            {Icon && <Icon className="h-3.5 w-3.5" aria-hidden="true" />}{label}
            {badge > 0 && !on && <span data-testid={badgeTestId} className="ml-0.5 rounded-full bg-[var(--hp-loss)] px-1.5 text-xs font-bold leading-4 text-white">{badge}</span>}
          </button>
          </Fragment>
        );
      })}
    </div>
  );
}

/** Convert the [[id, label, Icon], ...] arrays the pages already export into SubTabs' shape. */
export const asTabs = (list) => list.map(([id, label, icon]) => ({ id, label, icon }));

export function PageHeader({ eyebrow, title, description, actions, children, testId }) {
  return (
    <header className="space-y-4" data-testid={testId}>
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div className="min-w-0">
          {eyebrow && <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--hp-profit)] mb-1.5">{eyebrow}</p>}
          <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight text-[var(--hp-heading)]">{title}</h1>
          {description && <p className="text-sm text-[var(--hp-muted)] mt-2 max-w-3xl">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2.5 shrink-0">{actions}</div>}
      </div>
      {children}
    </header>
  );
}

export function SectionHeader({ title, description, actions, testId, as: Tag = "h2" }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3" data-testid={testId}>
      <div className="min-w-0">
        <Tag className="font-head text-2xl font-bold text-[var(--hp-heading)]">{title}</Tag>
        {description && <p className="text-sm text-[var(--hp-muted)] mt-1 max-w-3xl">{description}</p>}
      </div>
      {actions}
    </div>
  );
}
